import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Locations that may contain assets with circle graphics:
 * 1. root->sceneGroups->[i]->mediaAsset
 * 2. root->slides->[i]->children->[i]
 * 3. root->slides->[i]->stickers->[i]
 * 4. root->slides->[i]->subslides->[i]->children->[i]
 * 5. root->slides->[i]->textPositionPresets->[key]->[i]
 *
 * Each asset can have both colorGraphics and alphaGraphics lists.
 * We only migrate a CircleGraphic if it is the ONLY graphic in its list.
 */

function migrateGraphicsList(
    rootNode: Immutable.Map<string, unknown>,
    pathToGraphicsList: (string | number)[],
) {
    const graphicsList = rootNode.getIn(pathToGraphicsList) as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;

    if (!graphicsList || graphicsList.size !== 1) {
        return;
    }

    const graphic = graphicsList.get(0);
    const isCircleGraphic =
        graphic !== undefined &&
        graphic.has('graphic') &&
        graphic.get('graphic') === 'circle' &&
        graphic.has('shape');
    if (!isCircleGraphic) {
        return;
    }

    const shapePath = [...pathToGraphicsList, 0, 'shape'];
    rootNode.setIn([...shapePath, 'positionX'], 0);
    rootNode.setIn([...shapePath, 'positionY'], 0);

    const alignXPath = [...pathToGraphicsList, 0, 'alignGraphicX'];
    const alignYPath = [...pathToGraphicsList, 0, 'alignGraphicY'];
    rootNode.setIn(alignXPath, 'center');
    rootNode.setIn(alignYPath, 'center');
}

function migrateAsset(rootNode: Immutable.Map<string, unknown>, pathToAsset: (string | number)[]) {
    const asset = rootNode.getIn(pathToAsset) as Immutable.Map<string, unknown> | undefined;
    if (!asset) {
        return;
    }

    if (asset.has('colorGraphics')) {
        migrateGraphicsList(rootNode, [...pathToAsset, 'colorGraphics']);
    }

    if (asset.has('alphaGraphics')) {
        migrateGraphicsList(rootNode, [...pathToAsset, 'alphaGraphics']);
    }
}

function migrateSceneGroup(sg: Immutable.Map<string, unknown>) {
    return sg.withMutations((sceneGroup) => {
        // 1. root->sceneGroups->[i]->mediaAsset
        if (sceneGroup.has('mediaAsset')) {
            migrateAsset(sceneGroup, ['mediaAsset']);
        }
    });
}

export const migrationTo9_238 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            if (map.has('sceneGroups')) {
                map.get('sceneGroups').forEach((sceneGroup, i) => {
                    map.setIn(['sceneGroups', i], migrateSceneGroup(sceneGroup));
                });
            }
            map.get('slides').forEach((scene, i) => {
                map.setIn(['slides', i], this.migrateScene(scene));
            });
        });
    },
    migrateScene(s) {
        return s.withMutations((scene) => {
            // 2. root->slides->[i]->children->[i]
            if (scene.has('children')) {
                const children = scene.get('children') as Immutable.List<unknown>;
                children.forEach((_, i) => {
                    migrateAsset(scene, ['children', i]);
                });
            }

            // 3. root->slides->[i]->stickers->[i]
            if (scene.has('stickers')) {
                const stickers = scene.get('stickers') as Immutable.List<unknown>;
                stickers.forEach((_, i) => {
                    migrateAsset(scene, ['stickers', i]);
                });
            }

            // 4. root->slides->[i]->subslides->[i]->children->[i]
            if (scene.has('subslides')) {
                const subslides = scene.get('subslides') as Immutable.List<unknown>;
                subslides.forEach((subslide: Immutable.Map<string, unknown>, i) => {
                    if (subslide.has('children')) {
                        const children = subslide.get('children') as Immutable.List<unknown>;
                        children.forEach((_, j) => {
                            migrateAsset(scene, ['subslides', i, 'children', j]);
                        });
                    }
                });
            }

            // 5. root->slides->[i]->textPositionPresets->[key]->[i]
            if (scene.has('textPositionPresets')) {
                const presets = scene.get('textPositionPresets') as Immutable.Map<string, unknown>;
                presets.forEach((presetArray: Immutable.List<unknown>, presetKey: string) => {
                    presetArray.forEach((_, presetIndex: number) => {
                        migrateAsset(scene, ['textPositionPresets', presetKey, presetIndex]);
                    });
                });
            }
        });
    },
});
